/**
 * @fileoverview useUserPreferences Hook
 *
 * Provides easy access to user preferences with automatic persistence
 * to database, IndexedDB, and localStorage
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import type { UserPreferences } from '@xanectech/types';
import {
  getUserPreferences,
  setUserPreferences,
} from '../services/UserPreferencesService';
import { createLogger } from '@xanectech/lib';

const logger = createLogger('Auth - useUserPreferences');

export interface UseUserPreferencesReturn {
  preferences: UserPreferences;
  isLoading: boolean;
  error: Error | null;
  updatePreference: (key: string, value: unknown) => Promise<void>;
  updatePreferences: (prefs: Partial<UserPreferences>) => Promise<void>;
  setTheme: (theme: 'light' | 'dark' | 'system') => Promise<void>;
  setExpiry: (expiry: string) => Promise<void>;
}

/**
 * Hook to manage user preferences with sync across storage layers
 */
export function useUserPreferences(): UseUserPreferencesReturn {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<UserPreferences>({
    expiry: 'one-hour',
    theme: 'system',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Load preferences on mount or when user changes
  useEffect(() => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    const loadPreferences = async () => {
      try {
        setIsLoading(true);
        const loaded = await getUserPreferences(user.id);
        setPreferences(loaded);
        setError(null);
      } catch (err) {
        logger.error('Failed to load preferences', err as Error);
        setError(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    void loadPreferences();
  }, [user?.id]);

  const updatePreference = useCallback(
    async (key: string, value: unknown) => {
      if (!user?.id) {
        logger.warn('Cannot update preference: no user');
        return;
      }

      try {
        const updated = await setUserPreferences(user.id, {
          [key]: value,
        });
        setPreferences(updated);
      } catch (err) {
        logger.error('Failed to update preference', err as Error);
        setError(err as Error);
        throw err;
      }
    },
    [user?.id],
  );

  const updatePreferences = useCallback(
    async (prefs: Partial<UserPreferences>) => {
      if (!user?.id) {
        logger.warn('Cannot update preferences: no user');
        return;
      }

      try {
        const updated = await setUserPreferences(user.id, prefs);
        setPreferences(updated);
      } catch (err) {
        logger.error('Failed to update preferences', err as Error);
        setError(err as Error);
        throw err;
      }
    },
    [user?.id],
  );

  const setTheme = useCallback(
    async (theme: 'light' | 'dark' | 'system') => {
      await updatePreference('theme', theme);
    },
    [updatePreference],
  );

  const setExpiry = useCallback(
    async (expiry: string) => {
      await updatePreference('expiry', expiry);
    },
    [updatePreference],
  );

  return {
    preferences,
    isLoading,
    error,
    updatePreference,
    updatePreferences,
    setTheme,
    setExpiry,
  };
}
